"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover"

interface SchoolSelectProps {
  value: string
  onChange: (value: string) => void
  options: readonly string[]
  placeholder?: string
}

export function SchoolSelect({
  value,
  onChange,
  options,
  placeholder = "Select school...",
}: SchoolSelectProps) {
  const [open, setOpen] = React.useState(false)

  const filteredOptions = React.useMemo(
    () =>
      options.filter((s) =>
        s.toLowerCase().includes(value.trim().toLowerCase())
      ),
    [options, value]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <Input
            id="school"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="pr-10"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            aria-label="Toggle school list"
            onClick={() => setOpen((o) => !o)}
          >
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandList>
            <CommandEmpty>
              No matching school. You can use your typed school name.
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((school) => (
                <CommandItem
                  key={school}
                  value={school}
                  onSelect={() => {
                    onChange(school)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === school ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {school}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
